import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaFolderOpen,
  FaBarcode,
  FaPercentage,
  FaHistory,
  FaToggleOn,
  FaFilter,
} from "react-icons/fa";
import apiRequest from "../services/api";

const AssetCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeOnlyFilter, setActiveOnlyFilter] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [formData, setFormData] = useState({
    id: null,
    code: "",
    name: "",
    statutoryRatePercent: 20.0,
    usefulLifeYears: 5,
    depreciationMethod: "STRAIGHT_LINE",
    active: true,
  });

  const fetchCategories = async (activeOnly = activeOnlyFilter) => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await apiRequest(
        `assets/categories?activeOnly=${activeOnly}`
      );
      setCategories(resp.data || []);
    } catch (err) {
      setError(
        err?.message ||
          "Failed to synchronize asset category database registries."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(activeOnlyFilter);
  }, [activeOnlyFilter]);

  const handleRateChange = (rateVal) => {
    const rate = parseFloat(rateVal) || 0;
    const derivedYears = rate > 0 ? Math.round(100 / rate) : 0;

    setFormData((prev) => ({
      ...prev,
      statutoryRatePercent: rateVal,
      usefulLifeYears: derivedYears,
    }));
  };

  const openModal = (mode, category = null) => {
    setModalMode(mode);
    setSelectedCategory(category);
    setError("");

    if (category) {
      setFormData({
        id: category.id,
        code: category.code || "",
        name: category.name || "",
        statutoryRatePercent: category.statutoryRatePercent ?? 20.0,
        usefulLifeYears: category.usefulLifeYears ?? 5,
        depreciationMethod: category.depreciationMethod || "STRAIGHT_LINE",
        active: category.active ?? true,
      });
    } else {
      setFormData({
        id: null,
        code: "",
        name: "",
        statutoryRatePercent: 20.0,
        usefulLifeYears: 5,
        depreciationMethod: "STRAIGHT_LINE",
        active: true,
      });
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.code || formData.code.trim() === "") {
      setError("Category Code is explicitly required.");
      return;
    }

    if (!formData.name || formData.name.trim() === "") {
      setError("Category Name is explicitly required.");
      return;
    }

    const endpoint =
      modalMode === "create"
        ? "assets/categories"
        : `assets/categories/${formData.id}`;

    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          statutoryRatePercent: parseFloat(formData.statutoryRatePercent) || 0,
        },
      });

      fetchCategories();
      setIsModalOpen(false);
    } catch (err) {
      setError(
        err?.message || "Asset Category pipeline rejected schema transaction."
      );
    }
  };

  const handleDeactivate = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to deactivate the asset category "${name}"?`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`assets/categories/${id}`, {
        method: "DELETE",
      });

      fetchCategories();
    } catch (err) {
      setError(
        err?.message ||
          "Execution engine blocked category deactivation routing."
      );
    }
  };

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <FaFolderOpen className="text-white text-lg" />
            </div>

            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                Asset Categories
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Maintain fixed asset categories, depreciation methods, and
                statutory rates.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveOnlyFilter(!activeOnlyFilter)}
              className={`flex items-center gap-2 px-3 py-2.5 text-xs font-semibold rounded-xl border transition ${
                activeOnlyFilter
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-400"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900/40 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/[0.04]"
              }`}
            >
              <FaFilter className="text-[10px]" />
              {activeOnlyFilter ? "Active Only" : "Show All"}
            </button>

            <button
              onClick={() => openModal("create")}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
            >
              <FaPlus />
              New Category
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/40 dark:border-white/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                  <th className="px-6 py-4">Code</th>
                  <th className="px-6 py-4">Category Name</th>
                  <th className="px-6 py-4 text-center">Statutory Rate (%)</th>
                  <th className="px-6 py-4 text-center">Useful Life (Years)</th>
                  <th className="px-6 py-4 text-center">Depreciation Method</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-10 text-slate-500 dark:text-slate-500"
                    >
                      Querying asset category registry...
                    </td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center py-10 text-slate-500 dark:text-slate-500"
                    >
                      No asset categories registered.
                    </td>
                  </tr>
                ) : (
                  categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className="hover:bg-slate-50 transition-colors dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-6 py-4 font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        {cat.code}
                      </td>

                      <td className="px-6 py-4 font-semibold text-slate-800 dark:text-white">
                        {cat.name}
                      </td>

                      <td className="px-6 py-4 text-center font-mono font-semibold text-slate-700 dark:text-slate-200">
                        {Number(cat.statutoryRatePercent || 0).toFixed(2)} %
                      </td>

                      <td className="px-6 py-4 text-center font-mono text-slate-600 dark:text-slate-300">
                        {cat.usefulLifeYears} Yrs
                      </td>

                      <td className="px-6 py-4 text-center font-mono text-[10px] text-slate-500 uppercase tracking-wide dark:text-slate-400">
                        {cat.depreciationMethod || "STRAIGHT_LINE"}
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-medium tracking-wide border ${
                            cat.active
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                              : "bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
                          }`}
                        >
                          {cat.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openModal("view", cat)}
                            className="p-2 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition dark:bg-slate-900/60 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/[0.04] dark:hover:text-slate-200"
                            title="View Category Details"
                          >
                            <FaEye />
                          </button>

                          <button
                            onClick={() => openModal("edit", cat)}
                            className="p-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition dark:bg-blue-600/10 dark:border-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-600/20"
                            title="Modify Asset Category"
                          >
                            <FaEdit />
                          </button>

                          {cat.active && (
                            <button
                              onClick={() => handleDeactivate(cat.id, cat.name)}
                              className="p-2 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition dark:bg-red-600/10 dark:border-red-500/10 dark:text-red-400 dark:hover:bg-red-600/20"
                              title="Deactivate Category"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Sheet Modal */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
          onClick={() => setIsModalOpen(false)}
        />

        <div
          className={`relative w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(0,0,0,0.15)] transition-transform duration-300 transform space-y-6 max-h-[90vh] overflow-y-auto dark:bg-slate-900/95 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 capitalize flex items-center gap-2 dark:text-white">
                <FaFolderOpen className="text-emerald-500 text-sm" />
                {modalMode} Asset Category
              </h3>

              <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                {modalMode === "view"
                  ? "Inspecting immutable operational metadata segment."
                  : "Specify code index, tax rate %, and status flags."}
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Code + Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                  <FaBarcode className="text-[10px] text-slate-400" />
                  Category Code
                </label>

                <input
                  type="text"
                  required
                  placeholder="e.g. COMP, VEH, BLDG"
                  disabled={modalMode === "view" || modalMode === "edit"}
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value })
                  }
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 font-mono placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 transition dark:bg-slate-950/50 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                  <FaFolderOpen className="text-[10px] text-slate-400" />
                  Category Name
                </label>

                <input
                  type="text"
                  required
                  placeholder="e.g. Computer Equipment, Motor Vehicles"
                  disabled={modalMode === "view"}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 transition dark:bg-slate-950/50 dark:border-white/10 dark:text-white dark:placeholder-slate-600"
                />
              </div>
            </div>

            {/* Rate + Useful Life */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200 dark:bg-slate-950/30 dark:border-white/5">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                  <FaPercentage className="text-[10px] text-emerald-500" />
                  Statutory Rate (%)
                </label>

                <input
                  type="number"
                  step="0.01"
                  required
                  disabled={modalMode === "view"}
                  value={formData.statutoryRatePercent}
                  onChange={(e) => handleRateChange(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-emerald-600 text-right focus:outline-none focus:border-emerald-500/50 disabled:opacity-60 dark:bg-slate-950 dark:border-white/10 dark:text-emerald-400"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                  <FaHistory className="text-[10px] text-amber-500" />
                  Derived Useful Life (Years)
                </label>

                <input
                  type="number"
                  disabled
                  value={formData.usefulLifeYears}
                  className="w-full bg-slate-100 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-slate-600 text-right opacity-80 cursor-not-allowed dark:bg-slate-950/80 dark:border-white/5 dark:text-slate-300"
                />
              </div>
            </div>

            {/* Method + Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                  Depreciation Method
                </label>

                <select
                  disabled
                  value={formData.depreciationMethod}
                  className="w-full bg-slate-100 border border-slate-200 rounded-xl px-4 py-2.5 text-xs font-mono text-slate-600 cursor-not-allowed dark:bg-slate-950 dark:border-white/10 dark:text-slate-300"
                >
                  <option value="STRAIGHT_LINE">STRAIGHT_LINE</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1 dark:text-slate-400">
                  <FaToggleOn className="text-slate-400" />
                  Status
                </label>

                <select
                  disabled={modalMode === "view"}
                  value={formData.active ? "true" : "false"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      active: e.target.value === "true",
                    })
                  }
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:border-emerald-500/50 dark:bg-slate-950 dark:border-white/10 dark:text-white"
                >
                  <option value="true">ACTIVE</option>
                  <option value="false">INACTIVE</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create"
                    ? "Save Asset Category"
                    : "Apply Modifications"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetCategoryManagement;
